/**
 * JAISIU-XXXX — leaf module for fork-session-key detection.
 *
 * The webchat client persists session keys in several places
 * (`settings.sessionKey`, `settings.lastActiveSessionKey`, the
 * `chatOpenTabs` array, the `?session=` URL param, and the
 * `localStorage` read path in `storage.ts`). If the previous session
 * was a fork (`agent:<id>:fork:<uuid>`), rehydrating there would
 * land the operator's next message in a sidetracked conversation
 * that they cannot see in the sidebar.
 *
 * This module is a **leaf** (no imports from `app-settings.ts` or
 * `storage.ts`) so both layers can import it without creating an
 * import cycle. `app-settings.ts` re-exports the helpers for
 * backward compatibility with existing call sites; `storage.ts`
 * imports them directly.
 *
 * The match deliberately requires the slash-bucket pattern
 * `agent:<id>:fork:<uuid>` — user-named chats like
 * `agent:main:notes-about-forks` must NOT be redirected.
 *
 * The bare "main" string canonicalises to `agent:main:main` server-side
 * via `canonicalizeMainSessionAlias` in
 * `src/config/sessions/main-session.ts`, so seeding it routes the
 * next message back to the main session.
 */
export const PERSISTED_FORK_RE = /^agent:[^:]+:fork:/;

/** Cheap predicate: is this string a persisted fork key? */
export function isPersistedFork(value: string | null | undefined): boolean {
  return PERSISTED_FORK_RE.test((value ?? "").trim());
}

/**
 * Canonicalise a session key for rehydration. A fork collapses to
 * "main" (which the gateway resolves to `agent:main:main`). An empty
 * or missing value also falls back to "main". Any other key is
 * returned trimmed and unchanged.
 */
export function canonicalizeSessionKeyForRehydrate(value: string | null | undefined): string {
  const seed = (value ?? "").trim();
  if (PERSISTED_FORK_RE.test(seed)) {
    return "main";
  }
  return seed || "main";
}

/**
 * Same as {@link canonicalizeSessionKeyForRehydrate}, but never
 * short-circuits to "main" for an empty input. Used to seed
 * `applySessionKey`, which already falls back to "main" through
 * `applySettings`.
 */
export function guardPersistedSessionKey(value: string | null | undefined): string {
  const seed = (value ?? "").trim();
  if (PERSISTED_FORK_RE.test(seed)) {
    return "main";
  }
  return seed;
}
